import React from 'react';

import {
  styled,
  css,
  isPropValid,
  isPropNotEqualTo,
  itemCompactHeight,
  flexMinWidthZero,
  flex,
} from '@unfold/theme';

import {
  IGridItemProps,
  TCSSCreatorFunc,
  TCSSReturn,
  TOgGridItemStyledProps,
} from './types';

const FixedWidthItem: TCSSCreatorFunc = ({ width, theme }) => css`
  ${flexMinWidthZero}
  width: ${width};
  flex: 0 0 auto;
  min-height: ${itemCompactHeight.px()({ theme })};
`;

const AutoWidthItem: TCSSCreatorFunc = ({ theme }) => css`
  ${flexMinWidthZero}
  width: auto;
  flex: 0 0 auto;
  min-height: ${itemCompactHeight.px()({ theme })};
`;
const StretchedWidth: TCSSCreatorFunc = ({ theme }) => css`
  ${flexMinWidthZero}
  width: 100%;
  flex: 1 1 auto;
  min-height: ${itemCompactHeight.px()({ theme })};
`;

const FractionWidth: TCSSCreatorFunc = ({ fraction = undefined, theme }) => css`
  ${flexMinWidthZero}
  flex: 0 0 auto;
  width: ${`calc(100% / 12 * ${fraction})`};
  min-height: ${itemCompactHeight.px()({ theme })};
`;

const MinZeroWidth: TCSSCreatorFunc = () => css`
  min-width: 0;
`;

const HorizontalAndVerticalPadding: TCSSCreatorFunc = ({
  horizontalPadding = 0,
  verticalPadding = 0,
}) => css`
  padding: ${verticalPadding / 2}px ${horizontalPadding / 2}px;
`;

const CrossAxisAlign: TCSSCreatorFunc = ({ alignItems = 'center' }) => css`
  align-items: ${alignItems};
`;

const MainAxisAlign: TCSSCreatorFunc = ({ justifyContent = 'middle' }) => css`
  justify-content: ${justifyContent};
`;

const ItemDirection: TCSSCreatorFunc = ({ itemDirection = 'row' }) => css`
  flex-direction: ${itemDirection};
`;

const StyledItem = styled('div', {
  label: 'grid-item',
  shouldForwardProp: (prop) =>
    isPropValid(prop) && isPropNotEqualTo(prop, 'width'),
})<IGridItemProps>`
  ${flex}
  ${(props): TCSSReturn => props.width && FixedWidthItem}
  ${(props): TCSSReturn => props.isWidthAuto && AutoWidthItem}
  ${(props): TCSSReturn => props.isStretch && StretchedWidth}
  ${(props): TCSSReturn => props.fraction && FractionWidth}
  ${(props): TCSSReturn =>
    (props.horizontalPadding || props.verticalPadding) &&
    HorizontalAndVerticalPadding}
  ${(props): TCSSReturn => props.enableEllipsis && MinZeroWidth}
  ${(props): TCSSReturn => props.alignItems && CrossAxisAlign}
  ${(props): TCSSReturn => props.justifyContent && MainAxisAlign}
  ${(props): TCSSReturn => props.itemDirection && ItemDirection}
`;

export const GridItem: React.FC<TOgGridItemStyledProps> = ({
  alignItems = 'center',
  itemDirection = 'row',
  ...otherProps
}) => (
  <StyledItem
    alignItems={alignItems}
    itemDirection={itemDirection}
    {...otherProps}
  />
);
